import React, { useState } from 'react';
import { AlertCircle, Check, Eye, Film, Loader2, MessageSquare, Trash2, Upload } from 'lucide-react';
import { WelcomePlayer } from './WelcomePlayer';

export interface WelcomeHub {
  hubId: string;
  welcomeMessage: string | null;
  welcomeVideo: { sizeBytes: number; hasCaptions: boolean; uploadedAt: string } | null;
}

interface Props<H extends WelcomeHub> {
  hub: H;
  /** Calls the setup API: path relative to the hub, e.g. '/welcome-video' */
  call: <T>(path: string, method?: string, data?: unknown) => Promise<T>;
  onChanged: (hub: H) => void;
}

const MAX_VIDEO = 200 * 1024 * 1024;
const MAX_CAPTIONS = 512 * 1024;
const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const input = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const primaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';
const secondaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60';
const label = 'block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5';

/** PUT a file to a presigned S3 URL, reporting progress. */
function putFile(url: string, file: File, contentType: string, onProgress: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.send(file);
  });
}

export function WelcomeEditor<H extends WelcomeHub>({ hub, call, onChanged }: Props<H>) {
  const [message, setMessage] = useState(hub.welcomeMessage ?? '');
  const [video, setVideo] = useState<File | null>(null);
  const [captions, setCaptions] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [preview, setPreview] = useState<{ url: string; captions: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const saveMessage = () => run(async () => {
    const r = await call<{ hub: H }>('', 'PATCH', { welcomeMessage: message });
    onChanged(r.hub);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  });

  const upload = () => run(async () => {
    if (!video) return;
    if (video.type && video.type !== 'video/mp4') throw new Error('Choose an MP4 video.');
    if (video.size > MAX_VIDEO) throw new Error(`The video is ${mb(video.size)}; the limit is 200 MB.`);
    if (captions && captions.size > MAX_CAPTIONS) throw new Error('The captions file is larger than 512 KB.');
    const start = await call<{
      uploadId: string; video: { uploadUrl: string; contentType: string }; captions: { uploadUrl: string; contentType: string } | null;
    }>('/welcome-video', 'POST', { sizeBytes: video.size, ...(captions ? { captionsSizeBytes: captions.size } : {}) });
    setProgress(0);
    await putFile(start.video.uploadUrl, video, start.video.contentType, setProgress);
    if (captions && start.captions) await putFile(start.captions.uploadUrl, captions, start.captions.contentType, () => undefined);
    const r = await call<{ hub: H }>('/welcome-video/confirm', 'POST', { uploadId: start.uploadId });
    onChanged(r.hub);
    setVideo(null);
    setCaptions(null);
    setPreview(null);
  });

  const remove = () => run(async () => {
    const r = await call<{ hub: H }>('/welcome-video', 'DELETE');
    onChanged(r.hub);
    setPreview(null);
  });

  const showPreview = () => run(async () => {
    const r = await call<{ video: { url: string } | null; captions: string | null }>('/welcome');
    setPreview(r.video ? { url: r.video.url, captions: r.captions } : null);
  });

  const v = hub.welcomeVideo;

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 pt-4">
        <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase flex items-center gap-1.5"><Film size={14} /> Welcome</h4>
      </div>
      {error && (
        <div className="mx-5 mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      )}

      <div className="p-5 border-b border-[#e2e8f0]">
        <label className={label}><Film className="w-4 h-4 text-slate-400" />Welcome video</label>
        {v ? (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-600">
              {mb(v.sizeBytes)}{v.hasCaptions ? ', with captions' : ', no captions'} · added {new Date(v.uploadedAt).toLocaleDateString('en-GB')}
            </p>
            <div className="flex gap-2">
              <button type="button" className={secondaryBtn} disabled={busy} onClick={showPreview}><Eye size={16} /> Preview</button>
              <button type="button" className={secondaryBtn} disabled={busy} onClick={remove}><Trash2 size={16} /> Remove</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 mb-4">No video yet. Visitors see the message on its own.</p>
        )}
        {preview && <div className="mb-4 max-w-2xl"><WelcomePlayer url={preview.url} captions={preview.captions} /></div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">{v ? 'Replace with' : 'Video'} (MP4, up to 200 MB)</p>
            <input type="file" accept="video/mp4" className="block w-full text-sm text-slate-600" onChange={(e) => setVideo(e.target.files?.[0] ?? null)} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Captions (optional, WebVTT .vtt)</p>
            <input type="file" accept=".vtt,text/vtt" className="block w-full text-sm text-slate-600" onChange={(e) => setCaptions(e.target.files?.[0] ?? null)} />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button type="button" className={primaryBtn} disabled={busy || !video} onClick={upload}>
            {busy && progress !== null ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {progress !== null ? `Uploading ${Math.round(progress * 100)}%` : 'Upload video'}
          </button>
          {video && <span className="text-xs text-slate-400">{video.name} · {mb(video.size)}</span>}
        </div>
      </div>

      <div className="p-5">
        <label className={label}><MessageSquare className="w-4 h-4 text-slate-400" />Welcome message</label>
        <textarea
          className={`${input} resize-none`} rows={4} maxLength={2000} value={message}
          onChange={(e) => setMessage(e.target.value)} placeholder="A short note to the client team: what this page is for and what comes next."
        />
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-slate-400">{message.length}/2000</p>
          <button type="button" className={secondaryBtn} disabled={busy || message === (hub.welcomeMessage ?? '')} onClick={saveMessage}>
            {saved ? <Check size={16} /> : null} {saved ? 'Saved' : 'Save message'}
          </button>
        </div>
      </div>
    </div>
  );
}
